// Hält einen Wert innerhalb eines bestimmten Bereichs
function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

// Blendet den Ladeindikator ein oder aus
function toggleLoadingSpinner(image) {
  const loadingSpinner = image
    .closest('.product__modal-opener')
    ?.querySelector('.loading__spinner');

  if (loadingSpinner) {
    loadingSpinner.classList.toggle('hidden');
  }
}

// Bereitet die Zoom-Ebene vor
function prepareOverlay(container, imageSource) {
  container.className = 'image-magnify-full-size';
  container.setAttribute('aria-hidden', 'true');

  container.style.backgroundImage = `url("${imageSource}")`;
  container.style.backgroundColor = 'var(--gradient-background)';
  container.style.backgroundRepeat = 'no-repeat';

  container.style.touchAction = 'none';
  container.style.userSelect = 'none';
  container.style.webkitUserSelect = 'none';
  container.style.cursor = 'grab';
  container.style.overscrollBehavior = 'contain';
}

// Erstellt die Zoom-Ebene
function createOverlay(image, onReady) {
  const imageSource = image.currentSrc || image.src;
  const preloadImage = new Image();
  const overlay = document.createElement('div');

  prepareOverlay(overlay, imageSource);

  image.style.opacity = '0.5';
  toggleLoadingSpinner(image);

  preloadImage.onload = () => {
    // Bereits vorhandene Zoom-Ebenen dieses Bildes entfernen
    image.parentElement
      .querySelectorAll('.image-magnify-full-size')
      .forEach((existingOverlay) => existingOverlay.remove());

    image.parentElement.insertBefore(overlay, image);

    image.style.opacity = '1';
    toggleLoadingSpinner(image);

    if (typeof onReady === 'function') {
      onReady(overlay);
    }
  };

  preloadImage.onerror = () => {
    image.style.opacity = '1';
    toggleLoadingSpinner(image);
  };

  preloadImage.src = imageSource;

  return overlay;
}

// Aktiviert den Zoom und die Verschiebefunktion
function magnify(image, zoomRatio, initialEvent) {
  createOverlay(image, (overlay) => {
    let positionX = 0;
    let positionY = 0;

    let isDragging = false;
    let activePointerId = null;

    let startPointerX = 0;
    let startPointerY = 0;
    let startPositionX = 0;
    let startPositionY = 0;

    let maximumMovement = 0;
    let pointerStartTime = 0;

    function getMetrics() {
      const frameRect = overlay.getBoundingClientRect();

      const backgroundWidth = frameRect.width * zoomRatio;
      const backgroundHeight = frameRect.height * zoomRatio;

      return {
        frameRect,
        backgroundWidth,
        backgroundHeight,
        minX: Math.min(0, frameRect.width - backgroundWidth),
        minY: Math.min(0, frameRect.height - backgroundHeight),
      };
    }

    function setBackgroundPosition(x, y) {
      const metrics = getMetrics();

      positionX = clamp(x, metrics.minX, 0);
      positionY = clamp(y, metrics.minY, 0);

      overlay.style.backgroundSize =
        `${metrics.backgroundWidth}px ${metrics.backgroundHeight}px`;

      overlay.style.backgroundPosition =
        `${positionX}px ${positionY}px`;
    }

    function focusAtPointer(event) {
      const metrics = getMetrics();
      const rect = metrics.frameRect;

      if (!rect.width || !rect.height) return;

      const pointerX = clamp(
        event.clientX - rect.left,
        0,
        rect.width
      );

      const pointerY = clamp(
        event.clientY - rect.top,
        0,
        rect.height
      );

      const relativeX = pointerX / rect.width;
      const relativeY = pointerY / rect.height;

      const backgroundPointX =
        relativeX * metrics.backgroundWidth;

      const backgroundPointY =
        relativeY * metrics.backgroundHeight;

      setBackgroundPosition(
        pointerX - backgroundPointX,
        pointerY - backgroundPointY
      );
    }

    function closeZoom() {
      // Verhindert, dass der abschließende Touch-Klick
      // das darunterliegende Bild sofort erneut vergrößert
      image.dataset.zoomSuppressUntil = String(Date.now() + 500);

      overlay.remove();
    }

    // Der zuerst angeklickte Bereich wird vergrößert
    requestAnimationFrame(() => {
      focusAtPointer(initialEvent);
    });

    overlay.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
          return;
        }

        isDragging = true;
        activePointerId = event.pointerId;

        startPointerX = event.clientX;
        startPointerY = event.clientY;

        startPositionX = positionX;
        startPositionY = positionY;

        maximumMovement = 0;
        pointerStartTime = Date.now();

        overlay.style.cursor = 'grabbing';

        if (overlay.setPointerCapture) {
          overlay.setPointerCapture(event.pointerId);
        }

        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false }
    );

    overlay.addEventListener(
      'pointermove',
      (event) => {
        // Desktop: Ohne gedrückte Maustaste folgt
        // der Zoom weiterhin dem Mauszeiger
        if (event.pointerType === 'mouse' && !isDragging) {
          focusAtPointer(event);
          return;
        }

        if (
          !isDragging ||
          event.pointerId !== activePointerId
        ) {
          return;
        }

        const deltaX = event.clientX - startPointerX;
        const deltaY = event.clientY - startPointerY;

        const movement = Math.sqrt(
          deltaX * deltaX + deltaY * deltaY
        );

        maximumMovement = Math.max(
          maximumMovement,
          movement
        );

        setBackgroundPosition(
          startPositionX + deltaX,
          startPositionY + deltaY
        );

        event.preventDefault();
        event.stopPropagation();
      },
      { passive: false }
    );

    function finishPointer(event, cancelled = false) {
      if (event.pointerId !== activePointerId) {
        return;
      }

      if (
        overlay.hasPointerCapture &&
        overlay.hasPointerCapture(event.pointerId)
      ) {
        overlay.releasePointerCapture(event.pointerId);
      }

      const pointerDuration =
        Date.now() - pointerStartTime;

      /*
       * Kleine Fingerbewegungen bis 16 Pixel gelten als Tap.
       * Dadurch funktioniert das Schließen auf dem iPad zuverlässig,
       * obwohl der Finger beim Antippen nie völlig stillsteht.
       */
      const wasTap =
        maximumMovement <= 16 &&
        pointerDuration <= 700;

      isDragging = false;
      activePointerId = null;

      overlay.style.cursor = 'grab';

      event.preventDefault();
      event.stopPropagation();

      if (!cancelled && wasTap) {
        closeZoom();
      }
    }

    overlay.addEventListener(
      'pointerup',
      (event) => {
        finishPointer(event, false);
      },
      { passive: false }
    );

    overlay.addEventListener(
      'pointercancel',
      (event) => {
        finishPointer(event, true);
      },
      { passive: false }
    );

    /*
     * Verhindert, dass ein zusätzlicher Browser-Klick
     * an das darunterliegende Produktbild weitergereicht wird.
     */
    overlay.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
    });

    overlay.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    // Mit Escape kann der Zoom am Desktop geschlossen werden
    function closeWithEscape(event) {
      if (event.key === 'Escape' && overlay.isConnected) {
        closeZoom();
        document.removeEventListener(
          'keydown',
          closeWithEscape
        );
      }
    }

    document.addEventListener(
      'keydown',
      closeWithEscape
    );
  });
}

// Aktiviert den Zoom für alle vorgesehenen Produktbilder
function enableZoomOnHover(zoomRatio) {
  const images = document.querySelectorAll(
    '.image-magnify-hover'
  );

  images.forEach((image) => {
    // Verhindert doppelte Event-Listener
    if (image.dataset.magnifyInitialized === 'true') {
      return;
    }

    image.dataset.magnifyInitialized = 'true';

    image.addEventListener('click', (event) => {
      const suppressUntil = Number(
        image.dataset.zoomSuppressUntil || 0
      );

      if (Date.now() < suppressUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // Sicherheitsprüfung gegen doppelte Zoom-Ebenen
      const existingOverlay =
        image.parentElement.querySelector(
          '.image-magnify-full-size'
        );

      if (existingOverlay) {
        existingOverlay.remove();
        return;
      }

      magnify(image, zoomRatio, event);
    });
  });
}

enableZoomOnHover(2);